/**
 * Review and sign (Treatment Plan spec v1.4, S-22), drawn as canvas board 15.
 *
 * One panel. It lists only what is still open, each item with its one-step
 * fix inline: a reason chip, "Mark reviewed", a visit or PDMP date. Warnings
 * that never block are grey. Then the checks that passed, the attestation with
 * the patient's name, and "Sign and send": a passkey, or the 6-digit signing
 * PIN.
 *
 * Every check runs again on the server at signing (SG-003), so this screen is
 * a guide, never the gate. A plan built by a nurse account shows "Send to Dr.
 * ___ to sign" instead (S-23), because nobody signs for someone else.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '../lib/api';
import { useConnections } from './Connections';
import { LoadingRows, overlay } from './ui';
import { IconAlert, IconCheck, IconClose } from './icons';

export interface SignoffView {
  planId: string | null;
  version: number;
  status: string;
  patient: { name: string; firstName: string; state: string | null; stateName: string };
  clinicName: string | null;
  banner: { state: 'ready' | 'needs_you' | 'waiting'; label: string; count: number };
  open: Array<{ id: string; kind: string; ok: boolean; message: string; fix: string; why: string | null; fixKind: string; itemName: string | null; forSigner: boolean; item_id?: string | null }>;
  warnings: Array<{ id: string; message: string }>;
  passed: Array<{ id: string; label: string }>;
  licenseHeadsUp: { text: string } | null;
  route: { mode: 'sign' | 'send_to_sign' | 'view'; signerName: string; queueLabel: string | null; buttonLabel: string; note: string | null };
  attestation: { version: string; text: string; isTest: boolean } | null;
  items: Array<{
    itemId: string;
    name: string;
    needsReason: boolean;
    reason: { code: string; detail: string } | null;
    chartFacts: string | null;
    needsDeviationAck: boolean;
    deviationAcknowledged: boolean;
    deviationText: string | null;
  }>;
  flags: Array<{ id: string; itemId: string | null; text: string; detail: string; reviewed: boolean }>;
  safetyOutside: string | null;
  hrt: { required: boolean; on: string | null; type: string | null };
  chips: { reason: Array<{ code: string; short: string; label: string }>; deviation: Array<{ code: string; label: string }> };
  sign: { pinSet: boolean; passkeys: number };
  testTexts: boolean;
}

interface MeSigning {
  pin: { set: boolean; lockedUntil: string | null };
  terms: { version: string; text: string; approved: boolean; isTest: boolean; acceptedAt: string | null };
}

const today = () => new Date().toISOString().slice(0, 10);
const FOREST = 'var(--forest)';

/**
 * Board 15's card under the plan: "2 things need you" (amber) or "Ready to
 * sign" (green), with the Review and sign button inside it.
 */
export function SignBanner({
  banner,
  buttonLabel,
  onOpen,
  busy,
}: {
  banner: SignoffView['banner'] | null;
  buttonLabel?: string;
  onOpen?: () => void;
  busy?: boolean;
}) {
  if (!banner) return null;
  const ready = banner.state === 'ready';
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-[14px] border px-4 py-3.5 ${ready ? 'border-[#cfe3d8] bg-[#eaf4ee]' : 'border-[#f1dcae] bg-[#fdf3dc]'}`}
      data-testid="sign-banner"
      role="status"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-forest">
        <IconCheck size={18} />
      </span>
      {/* A floor on the text so a narrow column moves the button under it instead of squeezing the words. */}
      <div className="min-w-[150px] flex-1">
        <p className="font-display text-[21px] leading-tight" style={{ fontWeight: 560, color: ready ? '#1b3f35' : '#6b4a12' }}>
          {banner.label}
        </p>
        <p className="text-[13.5px] leading-snug" style={{ color: ready ? '#2f5d4c' : '#8a5a14' }}>
          {ready ? 'Every check has passed. Review and sign to send it.' : 'Open Review and sign to clear them. Everything else is done.'}
        </p>
      </div>
      {onOpen && (
        <button
          type="button"
          disabled={busy}
          onClick={onOpen}
          className="shrink-0 rounded-[10px] bg-forest px-5 py-2.5 text-[15px] font-semibold leading-tight text-white hover:bg-forest-deep disabled:opacity-60"
        >
          {buttonLabel ?? 'Review and sign'}
        </button>
      )}
    </div>
  );
}

/** A card for one open item: amber for something to answer, red for a safety flag. */
function OpenCard({ tone = 'amber', title, tag, children, testId }: { tone?: 'amber' | 'red'; title: ReactNode; tag?: string; children?: ReactNode; testId?: string }) {
  const red = tone === 'red';
  return (
    <div className={`rounded-[12px] border px-4 py-3.5 ${red ? 'border-[#f2c9c3] bg-[#fdf2f0]' : 'border-[#f1dcae] bg-[#fffaf0]'}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[14.5px] font-semibold leading-snug text-navy">{title}</p>
        {tag && <span className="shrink-0 text-[12px] font-semibold text-[#8a5a14]">{tag}</span>}
      </div>
      {children && <div className="mt-2.5">{children}</div>}
    </div>
  );
}

function Chip({ on, children, onClick, title }: { on: boolean; children: ReactNode; onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      title={title}
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium ${on ? 'border-forest bg-[#e6f0ea] text-forest' : 'border-[#d8d3c8] bg-white text-navy hover:bg-[#f6f5f1]'}`}
    >
      {children}
    </button>
  );
}

const field = 'h-11 w-full rounded-[10px] border border-[#d8d3c8] bg-white px-3 text-[14px] text-navy outline-none placeholder:text-[#9aa1a8] focus:border-forest';
const outline = 'shrink-0 rounded-[10px] border border-navy/80 bg-white px-4 py-2 text-[14px] font-semibold text-navy hover:bg-[#f6f5f1] disabled:opacity-50';

export function SignDrawer({ planId, patientId, onClose, onSent }: { planId: string; patientId: string; onClose: () => void; onSent: (text: string, tone: 'ok' | 'warn') => void }) {
  const qc = useQueryClient();
  const key = ['signoff', planId];
  const view = useQuery({ queryKey: key, queryFn: () => api.get<SignoffView>(`/signoff/plans/${planId}`) });
  const me = useQuery({ queryKey: ['signoff-me'], queryFn: () => api.get<MeSigning>('/signoff/me') });
  // Said before signing, not discovered after: what will not happen when this plan is sent.
  const offline = (useConnections('/credentials/connections').data?.connections ?? []).filter((c) => !c.connected && (c.key === 'email' || c.key === 'dosespot'));
  const [error, setError] = useState<string | null>(null);
  const [showPassed, setShowPassed] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** Every fix returns the fresh view, so the panel redraws from the server's answer. */
  const fix = useMutation({
    mutationFn: (call: () => Promise<unknown>) => call(),
    onSuccess: (fresh) => {
      setError(null);
      if (fresh && typeof fresh === 'object' && 'banner' in fresh) qc.setQueryData(key, fresh);
      else void qc.invalidateQueries({ queryKey: key });
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'That did not save'),
  });

  const after = (text: string, tone: 'ok' | 'warn') => {
    for (const k of [['treatment-queue'], ['workspace'], ['patient-plans'], ['nav-counts'], ['sign-queue']]) void qc.invalidateQueries({ queryKey: k });
    onSent(text, tone);
    onClose();
  };

  const v = view.data;
  const openCount = v ? v.banner.count || v.open.length : 0;
  return overlay(
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" role="dialog" aria-modal="true" aria-label="Review and sign" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mcos-slide-in flex h-full w-full max-w-[600px] flex-col bg-white shadow-[0_16px_48px_rgba(20,23,28,0.25)]" data-testid="sign-drawer">
        <header className="flex items-start justify-between gap-3 px-6 pb-2 pt-6">
          <h2 className="font-display text-[28px] leading-tight text-navy" style={{ fontWeight: 560 }}>
            Review and sign
          </h2>
          <div className="flex items-center gap-3">
            {v && (
              <span className="text-[13px] text-[#4d6072]">
                Plan v{v.version} · {v.patient.name}
                {v.patient.state ? ` · ${v.patient.state}` : ''}
              </span>
            )}
            <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-[#4d6072] hover:bg-mint">
              <IconClose size={20} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-6 pb-4 pt-2">
          {view.isLoading && <LoadingRows rows={4} />}
          {view.error && <p className="text-sm text-red-600">{view.error instanceof Error ? view.error.message : 'Could not load the checks'}</p>}
          {v && (
            <>
              <p className="sr-only" data-testid="sign-banner" role="status">{v.banner.label}</p>
              {v.open.length > 0 && (
                <>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#8a5a14]">Needs you ({openCount})</p>
                  {v.open.map((req) => (
                    <OpenItem key={`${req.id}-${req.item_id ?? ''}`} req={req} view={v} planId={planId} patientId={patientId} me={me.data} run={(call) => fix.mutate(call)} busy={fix.isPending} />
                  ))}
                </>
              )}

              {(v.warnings.length > 0 || v.licenseHeadsUp || offline.length > 0 || v.testTexts) && (
                <div className="space-y-1.5 rounded-[12px] bg-[#f2f1ec] px-4 py-3 text-[13px] leading-snug text-[#4d6072]">
                  {v.licenseHeadsUp && <p>{v.licenseHeadsUp.text}</p>}
                  {v.warnings.map((w) => (
                    <p key={w.id}>{w.message}</p>
                  ))}
                  {offline.map((c) => (
                    <p key={c.key} data-testid={`offline-${c.key}`}>
                      {c.label}: {c.note}
                    </p>
                  ))}
                  {v.testTexts && <p data-testid="test-texts">Test wording for local use. On the live site these texts wait for counsel's approval.</p>}
                </div>
              )}

              {v.passed.length > 0 && (
                <div>
                  <button type="button" className="flex w-full items-center justify-between py-1.5 text-[14px]" aria-expanded={showPassed} onClick={() => setShowPassed((x) => !x)}>
                    <span className="font-semibold text-navy">{v.passed.length} checks passed</span>
                    <span className="font-semibold text-navy">{showPassed ? 'Hide' : 'Show'}</span>
                  </button>
                  {showPassed && (
                    <ul className="mt-1 space-y-1" data-testid="checks-passed">
                      {v.passed.map((p) => (
                        <li key={p.id} className="flex gap-2 text-[13px] text-[#4d6072]">
                          <IconCheck size={14} className="mt-0.5 shrink-0 text-forest" />
                          {p.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {v.route.mode === 'sign' && v.attestation && (
                <p className="rounded-[12px] bg-[#e6f0ea] px-4 py-3.5 text-[14px] leading-relaxed text-navy" data-testid="attestation">
                  {v.attestation.text}
                </p>
              )}
              {v.route.note && <p className="text-[13px] text-[#4d6072]">{v.route.note}</p>}
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            </>
          )}
        </div>

        {v && (
          <footer className="px-6 pb-6 pt-2">
            {v.route.mode === 'sign' && <SignControls view={v} planId={planId} pinSet={me.data?.pin.set ?? v.sign.pinSet} onSigned={after} onPinSet={() => void me.refetch()} />}
            {v.route.mode === 'send_to_sign' && <SendToSign view={v} planId={planId} onDone={after} />}
            {v.route.mode === 'view' && <p className="text-[13.5px] text-[#4d6072]">{v.route.buttonLabel}</p>}
          </footer>
        )}
      </div>
    </div>,
  );
}

/** One open requirement with its one-step fix (FIX_KIND). */
function OpenItem({
  req,
  view,
  planId,
  patientId,
  me,
  run,
  busy,
}: {
  req: SignoffView['open'][number];
  view: SignoffView;
  planId: string;
  patientId: string;
  me: MeSigning | undefined;
  run: (call: () => Promise<unknown>) => void;
  busy: boolean;
}) {
  const title = req.itemName ? `${req.itemName}: ${req.message.replace(new RegExp(`^${req.itemName}:\\s*`), '')}` : req.message;

  switch (req.fixKind) {
    case 'reason': {
      const items = view.items.filter((i) => i.needsReason && (!req.item_id || i.itemId === req.item_id));
      return (
        <>
          {items.map((item) => (
            <ReasonFix
              key={item.itemId}
              item={item}
              firstName={view.patient.firstName}
              chips={view.chips.reason}
              busy={busy}
              onSave={(code, detail) => run(() => api.put(`/signoff/plans/${planId}/items/${item.itemId}/reason`, { code, detail }))}
            />
          ))}
        </>
      );
    }
    case 'ack_deviation': {
      const items = view.items.filter((i) => i.needsDeviationAck && !i.deviationAcknowledged);
      return (
        <>
          {items.map((item) => (
            <OpenCard key={item.itemId} title={`${item.name}: ${item.deviationText ?? 'not a protocol match'}`} testId={`fix-deviation-${item.itemId}`}>
              <AckFix chips={view.chips.deviation} busy={busy} onAck={(reason, note) => run(() => api.post(`/signoff/plans/${planId}/acknowledge`, { kind: 'deviation', id: item.itemId, reason, note }))} />
            </OpenCard>
          ))}
        </>
      );
    }
    case 'ack_flag': {
      const flags = view.flags.filter((f) => !f.reviewed);
      return (
        <>
          {flags.map((flag) => (
            <div key={flag.id} className="flex items-center gap-3 rounded-[12px] border border-[#f2c9c3] bg-[#fdf2f0] px-4 py-3.5" data-testid={`fix-flag-${flag.id}`}>
              <IconAlert size={20} className="shrink-0 text-alert" />
              <div className="min-w-0 flex-1">
                <p className="text-[14.5px] font-semibold leading-snug text-navy">Safety flag: {flag.text}</p>
                {flag.detail && <p className="text-[13px] text-[#4d6072]">Protocol library: {flag.detail}</p>}
              </div>
              <button type="button" className={outline} disabled={busy} onClick={() => run(() => api.post(`/signoff/plans/${planId}/acknowledge`, { kind: 'safety_flag', id: flag.id }))}>
                Mark reviewed
              </button>
            </div>
          ))}
        </>
      );
    }
    case 'safety_outside':
      return (
        <OpenCard tone="red" title={title} testId="fix-safety-outside">
          <AckFix label="Reviewed outside MCOS" busy={busy} onAck={(_reason, note) => run(() => api.post(`/signoff/plans/${planId}/acknowledge`, { kind: 'safety_data_outside', id: 'SD:outside', note }))} />
        </OpenCard>
      );
    case 'visit':
      return (
        <OpenCard title={title} tag="Needed" testId="fix-visit">
          <DateFix
            busy={busy}
            options={[['in_person', 'In person'], ['video', 'Video'], ['phone', 'Phone'], ['async', 'Messages']]}
            actionLabel="Log visit"
            onSave={(on, type) => run(() => api.post(`/signoff/patients/${patientId}/visits`, { visitOn: on, visitType: type, planId }))}
          />
        </OpenCard>
      );
    case 'pdmp':
      return (
        <OpenCard title={title} tag="Needed" testId="fix-pdmp">
          <DateFix busy={busy} options={null} actionLabel="Log PDMP check" onSave={(on) => run(() => api.post(`/signoff/patients/${patientId}/pdmp`, { state: view.patient.state ?? '', checkedOn: on, planId }))} />
        </OpenCard>
      );
    case 'hrt':
      return (
        <OpenCard title={title} tag="Needed" testId="fix-hrt">
          <DateFix
            busy={busy}
            options={[['phone', 'Phone'], ['video', 'Video'], ['in_person', 'In person']]}
            actionLabel="Log conversation"
            onSave={(on, type) => run(() => api.post(`/signoff/plans/${planId}/hrt-conversation`, { on, type }))}
          />
        </OpenCard>
      );
    case 'terms':
      return (
        <OpenCard title={title} tag="Needed" testId="fix-terms">
          {me?.terms.approved ? (
            <TermsFix terms={me.terms} busy={busy} onAccept={() => run(() => api.post('/signoff/me/terms', { version: me.terms.version }).then(() => api.get(`/signoff/plans/${planId}`)))} />
          ) : (
            <p className="text-[13px] text-[#4d6072]">Waiting for MCOS to approve the terms. Nothing for you to do.</p>
          )}
        </OpenCard>
      );
    case 'settings':
      return (
        <OpenCard title={title} tag="Needed" testId={`fix-settings-${req.id}`}>
          <Link to="/settings/credentials" className="text-[13.5px] font-semibold text-forest underline">
            Open Credentials and coverage
          </Link>
        </OpenCard>
      );
    case 'remove_item':
      return (
        <OpenCard tone="red" title={title} testId={`fix-remove-${req.id}`}>
          <p className="text-[13px] text-[#4d6072]">Untick this item in the plan to continue.</p>
        </OpenCard>
      );
    default:
      return (
        <OpenCard title={title} testId={`fix-none-${req.id}`}>
          {req.why && <p className="text-[13px] text-[#4d6072]">{req.why}</p>}
        </OpenCard>
      );
  }
}

/**
 * SC-06, board 15: "Tirzepatide: why a compounded version for John?" with the reason chips and one line of detail,
 * pre-filled from the chart. Saved when both are given, so there is no extra button to press.
 */
function ReasonFix({
  item,
  firstName,
  chips,
  busy,
  onSave,
}: {
  item: SignoffView['items'][number];
  firstName: string;
  chips: SignoffView['chips']['reason'];
  busy: boolean;
  onSave: (code: string, detail: string) => void;
}) {
  const [code, setCode] = useState(item.reason?.code ?? '');
  const [detail, setDetail] = useState(item.reason?.detail ?? item.chartFacts ?? '');
  const valid = Boolean(code) && detail.trim().length >= 20;
  const save = (c = code, d = detail) => {
    if (c && d.trim().length >= 20 && !busy && (c !== item.reason?.code || d.trim() !== item.reason?.detail)) onSave(c, d.trim());
  };
  return (
    <OpenCard title={`${item.name}: why a compounded version for ${firstName}?`} tag={item.reason ? undefined : 'Needed'} testId={`fix-reason-${item.itemId}`}>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Reason for ${item.name}`}>
        {chips.map((chip) => (
          <Chip
            key={chip.code}
            on={code === chip.code}
            title={chip.label}
            onClick={() => {
              setCode(chip.code);
              save(chip.code, detail);
            }}
          >
            {chip.short}
          </Chip>
        ))}
      </div>
      <input
        aria-label={`Detail for ${item.name}`}
        className={`${field} mt-2.5`}
        placeholder="Pick the reason that applies."
        value={detail}
        maxLength={500}
        onChange={(e) => setDetail(e.target.value)}
        onBlur={() => save()}
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-[12px] text-[#4d6072]">Pre-filled from the chart; edit as needed. Generic, cost-based or copied reasons are sent back.</p>
        {valid && !item.reason && (
          <button type="button" className="shrink-0 text-[13px] font-semibold text-forest underline" onClick={() => save()}>
            Save reason
          </button>
        )}
      </div>
    </OpenCard>
  );
}

function AckFix({
  chips,
  label = 'Mark reviewed',
  busy,
  onAck,
}: {
  chips?: Array<{ code: string; label: string }>;
  label?: string;
  busy: boolean;
  onAck: (reason: string | null, note: string | null) => void;
}) {
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  return (
    <div className="space-y-2.5">
      {chips && (
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Why, optional">
          {chips.map((chip) => (
            <Chip key={chip.code} on={reason === chip.code} onClick={() => setReason(reason === chip.code ? null : chip.code)}>
              {chip.label}
            </Chip>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input aria-label="Note, optional" className={field} placeholder="Note, optional" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        <button type="button" className={outline} disabled={busy} onClick={() => onAck(reason, note.trim() || null)}>
          {label}
        </button>
      </div>
    </div>
  );
}

function DateFix({
  options,
  actionLabel,
  busy,
  onSave,
}: {
  options: Array<[string, string]> | null;
  actionLabel: string;
  busy: boolean;
  onSave: (on: string, type: string) => void;
}) {
  const [on, setOn] = useState(today());
  const [type, setType] = useState(options?.[0]?.[0] ?? '');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input type="date" aria-label="Date" className={`${field} w-auto`} value={on} max={today()} onChange={(e) => setOn(e.target.value)} />
      {options && (
        <select aria-label="Type" className={`${field} w-auto`} value={type} onChange={(e) => setType(e.target.value)}>
          {options.map(([value, text]) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </select>
      )}
      <button type="button" className={outline} disabled={!on || busy} onClick={() => onSave(on, type)}>
        {actionLabel}
      </button>
    </div>
  );
}

function TermsFix({ terms, busy, onAccept }: { terms: MeSigning['terms']; busy: boolean; onAccept: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <button type="button" className="text-[13.5px] font-semibold text-forest underline" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Hide the Prescriber Terms' : 'Read the Prescriber Terms'}
      </button>
      {open && <div className="max-h-[200px] overflow-y-auto whitespace-pre-line rounded bg-[#f5f8fb] p-2 text-[12px] leading-relaxed">{terms.text}</div>}
      <button type="button" className={outline} disabled={busy} onClick={onAccept}>
        I accept the Prescriber Terms
      </button>
    </div>
  );
}

/**
 * "Sign and send", board 15: one wide button (Face ID, Touch ID or a passkey), and "Use your signing PIN" beneath for
 * a device without a passkey. While anything is open the button says how many are left.
 */
function SignControls({
  view,
  planId,
  pinSet,
  onSigned,
  onPinSet,
}: {
  view: SignoffView;
  planId: string;
  pinSet: boolean;
  onSigned: (text: string, tone: 'ok' | 'warn') => void;
  onPinSet: () => void;
}) {
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const hasPasskey = view.sign.passkeys > 0;
  const [usePin, setUsePin] = useState(!hasPasskey);
  const ready = view.banner.state === 'ready';
  const left = view.banner.count || view.open.length;

  const done = (result: { messageSent: boolean; messageError: string | null }) => {
    const name = view.patient.name;
    if (!result.messageSent && result.messageError) onSigned(`Signed and sent to ${name}'s portal, but they were not told: ${result.messageError}.`, 'warn');
    else onSigned(`Signed and sent to ${name}.`, 'ok');
  };
  const sign = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ messageSent: boolean; messageError: string | null }>(`/signoff/plans/${planId}/sign`, body),
    onSuccess: done,
  });
  const savePin = useMutation({
    mutationFn: () => api.put('/signoff/me/pin', { pin: newPin }),
    onSuccess: () => {
      setNewPin('');
      setConfirmPin('');
      onPinSet();
    },
  });
  const withPasskey = useMutation({
    mutationFn: async () => {
      const options = await api.get<Parameters<typeof startAuthentication>[0]['optionsJSON']>(`/signoff/plans/${planId}/sign-options`);
      const response = await startAuthentication({ optionsJSON: options });
      return api.post<{ messageSent: boolean; messageError: string | null }>(`/signoff/plans/${planId}/sign`, { type: 'passkey', response });
    },
    onSuccess: done,
  });
  const failure = sign.error ?? withPasskey.error ?? savePin.error;
  const pending = sign.isPending || withPasskey.isPending;

  return (
    <div className="space-y-2.5">
      {usePin && !pinSet ? (
        <div className="space-y-2 rounded-[12px] bg-[#f2f1ec] px-4 py-3" data-testid="set-pin">
          <p className="text-[13.5px] text-navy">Set a 6-digit signing PIN. You enter it each time you sign.</p>
          <div className="flex gap-2">
            <input aria-label="New signing PIN" inputMode="numeric" autoComplete="new-password" type="password" maxLength={6} className={field} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))} />
            <input aria-label="Repeat the signing PIN" inputMode="numeric" autoComplete="new-password" type="password" maxLength={6} className={field} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))} />
            <button type="button" className={outline} disabled={newPin.length !== 6 || newPin !== confirmPin || savePin.isPending} onClick={() => savePin.mutate()}>
              Save PIN
            </button>
          </div>
          {confirmPin.length === 6 && confirmPin !== newPin && <p className="text-[12.5px] text-red-600">The two PINs differ.</p>}
        </div>
      ) : (
        usePin && (
          <input
            aria-label="Signing PIN"
            inputMode="numeric"
            autoComplete="off"
            type="password"
            maxLength={6}
            placeholder="6-digit signing PIN"
            className={`${field} text-center tracking-[0.4em]`}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && ready && pin.length === 6 && sign.mutate({ type: 'pin', pin })}
          />
        )
      )}
      <button
        type="button"
        disabled={!ready || pending || (usePin && (!pinSet || pin.length !== 6))}
        onClick={() => (usePin ? sign.mutate({ type: 'pin', pin }) : withPasskey.mutate())}
        className="h-[52px] w-full rounded-[12px] text-[16.5px] font-semibold text-white transition-colors disabled:cursor-not-allowed"
        style={{ background: ready ? FOREST : '#9aa5ad' }}
      >
        {pending ? 'Signing' : ready ? 'Sign and send' : `Sign and send (clear ${left} first)`}
      </button>
      <p className="text-center text-[13px] text-[#4d6072]">
        {usePin ? (
          hasPasskey ? (
            <button type="button" className="font-semibold text-navy underline" onClick={() => setUsePin(false)}>
              Use your passkey instead
            </button>
          ) : (
            'Signing with your 6-digit PIN. Add a passkey in Credentials to use Face ID or Touch ID.'
          )
        ) : (
          <>
            Face ID, Touch ID or passkey. No passkey on this device?{' '}
            <button type="button" className="font-semibold text-navy underline" onClick={() => setUsePin(true)}>
              Use your signing PIN
            </button>
          </>
        )}
      </p>
      {failure && (
        <p className="text-center text-[12.5px] text-red-600" role="alert">
          {failure instanceof Error ? failure.message : 'Could not sign'}
        </p>
      )}
    </div>
  );
}

function SendToSign({ view, planId, onDone }: { view: SignoffView; planId: string; onDone: (text: string, tone: 'ok' | 'warn') => void }) {
  const send = useMutation({
    mutationFn: () => api.post<{ queueUserName: string }>(`/signoff/plans/${planId}/send-to-sign`),
    onSuccess: (result) => onDone(`Sent to ${result.queueUserName} to sign. ${view.patient.firstName} sees nothing until it is signed.`, 'ok'),
  });
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={view.banner.state !== 'ready' || send.isPending}
        onClick={() => send.mutate()}
        className="h-[52px] w-full rounded-[12px] text-[16.5px] font-semibold text-white disabled:cursor-not-allowed"
        style={{ background: view.banner.state === 'ready' ? FOREST : '#9aa5ad' }}
      >
        {view.route.buttonLabel}
      </button>
      {send.error && <p className="text-center text-[12.5px] text-red-600" role="alert">{send.error instanceof Error ? send.error.message : 'Could not send'}</p>}
    </div>
  );
}
